/**
 * P7(a) — coach-first athlete provisioning fork.
 *
 * Launched from the step-0 role fork ("I'm the coach") or the Step 1
 * "Coaching a team? Set up your team instead →" link. A two-screen mini-flow
 * that skips ALL athlete onboarding (no PAR-Q / pace / body metrics):
 *
 *   Screen A — "Set up your team"   (name / sport / season)
 *   Screen B — "Add your athletes"  (emails → pending invite rows)
 *
 * A1 chrome conformance (founder smoke finding #1): both screens render in
 * the ONBOARDING step chrome — StepLayout primitives inside the host's
 * OnboardingShell — not the team-page modal/sheet chrome. Same form logic as
 * CreateTeamModal / InviteAthletesModal (which keep their chrome for
 * team-page use).
 *
 * On finish it persists the durable `_account_role='coach'` flag (so the
 * account never re-enters athlete onboarding — mirrors the guardian fork)
 * and lands the coach on /v2/team with their pending "Invited" rows. No
 * athlete artifacts are written; athlete onboarding runs later only if they
 * train themselves.
 *
 * ⚖️ ORDER IS LOAD-BEARING (onboarding-role lane, 2026-08-02): before the
 * team is created, the fork AWAITS the server-side coach-role claim
 * (`useClaimCoachRole` → POST /api/user/role → `users.role='trainer'`),
 * because `users.role` is what the team-creation coach gate
 * (`team_create_coach_only_v1`) consults. A failed claim ABORTS creation
 * with the surfaced error — creating a team the gate would disown is the
 * exact defect this lane fixed. `_account_role` (client routing flag)
 * remains best-effort in finish(); do not confuse the two.
 * Pinned by tests/team-create-coach-only.test.js + CoachOnboarding.test.tsx.
 *
 * ⚖️ B209 (2026-09-20, "band on the coach path") — AGE BAND FIRST, ahead of
 * the role claim above. The age step (REL-29) always precedes this fork, so
 * `birthYear`/`birthMonth` are already answered by the time this component
 * mounts; the onboarding route passes them in. `handleCreate` persists that
 * pair to the server (`usePersistAccountRole`'s `persistAgeBand`) BEFORE
 * `claimCoachRole`/`createTeam`, blocking, not best-effort — with
 * `age_band_required_v1` on, a brand-new coach owns no team yet, so the
 * unknown-age wall's membership-backed exemption cannot apply, and an
 * unpersisted band would deadlock the coach's own first team create. A
 * missing pair (a stale device blob, a deep link) never reaches the wall at
 * all: `onCancel` sends the coach back to the age step, because the route's
 * own `ageStepDue` check is what actually renders it.
 * Pinned by tests/b209-coach-band.test.js + CoachOnboarding.test.tsx.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  StepCta,
  StepHeading,
  StepSub,
  TertiaryLink,
} from '../OnboardingShell/StepLayout';
import { useTeamActions, type CreatedTeam, type TeamActionError } from '../../hooks/useTeamActions';
import { useClaimCoachRole } from '../../hooks/useClaimCoachRole';
import { usePersistAccountRole } from '../../hooks/usePersistAccountRole';
import { inviteCapProblem, parseInviteEmails } from '../../lib/team/inviteEmails';
import { ReplanRefusalLine } from '../plan/ReplanRefusalLine';
import { AGE_BELOW_MINIMUM_MESSAGE, editAgeBelowMinimum, GUARDIAN_HOLD_MESSAGE } from '../../lib/planEdit';
import styles from './CoachOnboarding.module.css';

export interface CoachOnboardingProps {
  /** Back to the fork / journey screen (coach cancelled at Screen A). */
  onCancel: () => void;
  /**
   * B209 — the wizard's already-answered birth pair (REL-29's age step
   * precedes this fork). Undefined only in a defensive edge case (a stale
   * device blob, a deep link); `handleCreate` then bounces to `onCancel`
   * instead of attempting a team create the wall would refuse.
   */
  birthYear?: number;
  birthMonth?: number;
}

const SPORT_OPTIONS = [
  { value: 'cross_country', label: 'Cross-Country' },
  { value: 'track_field', label: 'Track & Field' },
  { value: 'road_running', label: 'Road Running' },
  { value: 'trail', label: 'Trail' },
];

// A1-ratified helper copy (verbatim; matches InviteAthletesModal).
const INVITE_HELPER =
  'No invite codes needed. Each athlete gets your invite when they first sign in with this email, and joins once they accept. Invites expire in 14 days.';

export function CoachOnboarding({ onCancel, birthYear, birthMonth }: CoachOnboardingProps) {
  const navigate = useNavigate();
  const { createTeam, inviteAthletes } = useTeamActions();
  const { claimCoachRole } = useClaimCoachRole();
  const { persistAccountRole, persistAgeBand } = usePersistAccountRole();
  const [team, setTeam] = useState<CreatedTeam | null>(null);

  // Screen A state.
  const [name, setName] = useState('');
  const [sport, setSport] = useState<string>('cross_country');
  const [season, setSeason] = useState('');
  // Screen B state.
  const [emailText, setEmailText] = useState('');
  const emails = useMemo(() => parseInviteEmails(emailText), [emailText]);
  // B443 — the server takes the first 50 addresses of a request and drops the
  // rest silently; past that the screen names the limit and waits.
  const capProblem = inviteCapProblem(emails);

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // B205 fix3 — a guardian-consent hold on the create renders through the
  // same quiet line the join/invite surfaces use (ReplanRefusalLine +
  // GUARDIAN_HOLD_MESSAGE, `lib/planEdit`), not the errorChip. Any other
  // failure (including a claimCoachRole refusal) keeps the existing chip.
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined);

  const finish = async () => {
    // Best-effort role flag — a lost write just means the next /today visit
    // routes via coach-mode (team ownership) instead of the flag, so never
    // block the coach from reaching their team on it.
    try {
      await persistAccountRole('coach');
    } catch {
      /* non-fatal — coach-mode still routes them to /team */
    }
    navigate('/team', { replace: true });
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed || pending) return;
    if (typeof birthYear !== 'number' || typeof birthMonth !== 'number') {
      // B209 — no answered pair reached this fork (a stale device blob, a
      // deep link). Never attempt a team create the wall would refuse:
      // `onCancel` clears the route's `coachPath` flag, and its own
      // `ageStepDue` check renders the age step, because `ageAnswered` is
      // still false.
      onCancel();
      return;
    }
    setPending(true);
    setError(null);
    setErrorCode(undefined);
    // ⚖️ AGE BAND FIRST (B209) — ahead of the role claim, and NOT
    // best-effort (unlike the `_account_role` flag in finish()). With
    // `age_band_required_v1` on, a brand-new coach owns no team yet, so
    // the wall's membership-backed exemption cannot apply — the unknown-age
    // wall would otherwise refuse this coach's own first team create. A
    // refused pair (403 `age_below_minimum`, the RULE 1 floor) stops here;
    // nothing below runs.
    try {
      await persistAgeBand(birthYear, birthMonth);
    } catch (err) {
      setErrorCode((err as { code?: string } | undefined)?.code);
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setPending(false);
      return;
    }
    // ⚖️ ROLE FIRST, TEAM SECOND — and the role claim is NOT best-effort.
    // `users.role='trainer'` must be durable before the team exists; if the
    // claim fails we surface the typed error and create NOTHING (the team
    // would otherwise belong to an account the coach gate refuses). The
    // claim is idempotent server-side, so a retry re-runs both halves safely.
    try {
      await claimCoachRole();
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Could not set up your coach account. Please try again.',
      );
      setPending(false);
      return;
    }
    try {
      const created = await createTeam({
        name: trimmed,
        sport: sport || null,
        season: season.trim() || null,
      });
      setTeam(created);
    } catch (err) {
      setErrorCode((err as TeamActionError | undefined)?.code);
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setPending(false);
    }
  };

  const handleInvite = async () => {
    if (!team || pending || emails.length === 0 || capProblem) return;
    setPending(true);
    setError(null);
    try {
      await inviteAthletes(team.id, emails);
      await finish();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setPending(false);
    }
  };

  if (!team) {
    return (
      <>
        <StepHeading>Set up your team</StepHeading>
        <StepSub>You will be the team&rsquo;s coach.</StepSub>

        <div className={styles.fields}>
          <label className={styles.field}>
            <span className={styles.label}>Team name</span>
            <input
              className={styles.input}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Spring 2026 XC"
              maxLength={100}
              disabled={pending}
              autoFocus
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Sport</span>
            <select
              className={styles.input}
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              disabled={pending}
            >
              {SPORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Season</span>
            <input
              className={styles.input}
              type="text"
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              placeholder="e.g. Spring 2026"
              maxLength={100}
              disabled={pending}
            />
          </label>
        </div>

        {errorCode === 'guardian_consent_required' ? (
          <ReplanRefusalLine message={GUARDIAN_HOLD_MESSAGE} />
        ) : editAgeBelowMinimum({ code: errorCode }) ? (
          <ReplanRefusalLine message={AGE_BELOW_MINIMUM_MESSAGE} />
        ) : (
          error && (
            <p className={styles.errorChip} role="alert">
              {error}
            </p>
          )
        )}

        <StepCta onClick={() => void handleCreate()} disabled={pending || !name.trim()}>
          {pending ? 'Creating team…' : 'Continue'}
        </StepCta>
        <TertiaryLink onClick={onCancel}>← Back</TertiaryLink>
      </>
    );
  }

  return (
    <>
      <StepHeading>Add your athletes</StepHeading>
      <StepSub>{INVITE_HELPER}</StepSub>

      <div className={styles.fields}>
        <label className={styles.field}>
          <span className={styles.label}>Athlete emails</span>
          <textarea
            className={styles.input}
            value={emailText}
            onChange={(e) => setEmailText(e.target.value)}
            placeholder="anna@example.com, ben@example.com"
            rows={4}
            aria-invalid={capProblem ? true : undefined}
            aria-describedby={capProblem ? 'coach-invite-cap-problem' : undefined}
            disabled={pending}
            autoFocus
          />
        </label>
      </div>

      {capProblem && (
        <p id="coach-invite-cap-problem" className={styles.errorChip} role="alert">
          {capProblem}
        </p>
      )}

      {error && (
        <p className={styles.errorChip} role="alert">
          {error}
        </p>
      )}

      <StepCta
        onClick={() => void handleInvite()}
        disabled={pending || emails.length === 0 || !!capProblem}
      >
        {pending ? 'Sending invites…' : `Invite ${emails.length} athletes`}
      </StepCta>
      <TertiaryLink onClick={() => void finish()}>
        I&rsquo;ll add them later
      </TertiaryLink>
    </>
  );
}
